// Copyright ©️ 2026 eVolve MEP, LLC

import { useState, useEffect, useMemo, useCallback } from 'react';

import type { StrongOmit } from '@evolvemep/api-ts';
import { type TextInputProps, TextInput } from '@mantine/core';

import { convertInchesToImperialString, hasValidTolerance } from 'helpers/convertInchesToImperialString';
import { isNotNil } from 'helpers/isNotNil';

type BaseInchesInputProps = {
  value?: number | null;
  defaultValue?: number | null;
  onChange: (value: number | null) => void;
  /**
   * If `true`, then `value` is represented as decimal feet, not decimal inches.
   *
   * Some data from Revit is stored in decimal feet,
   * but is only ever displayed/edited in inches.
   *
   * @defaultValue false
   */
  valueInFeet?: boolean;
  /** called to alert the parent if the input is valid or not */
  onValidChange?: (validValue: boolean) => void;
  /**
   * If `true`, allows negative measurements.
   *
   * @defaultValue false
   */
  allowNegative?: boolean;
  /**
   * If `true`, allows blank/null measurements.
   *
   * @defaultValue false
   */
  allowBlank?: boolean;
  /**
   * If provided, blurring the input while it is empty will call `onChange` with this value
   * instead of reverting to the previous valid measurement.
   *
   * In the same unit as `value`.
   *
   * @remarks Ignored if `allowBlank` is `true`.
   */
  blankValue?: number;
} & StrongOmit<TextInputProps, 'value' | 'defaultValue' | 'onChange'>;

type FractionalModeProps = {
  /**
   * The format that the measurement will be displayed in after validation.
   * Allowed values are "fraction" and "decimal".
   *
   * @defaultValue "fraction"
   */
  valueFormat?: 'fraction';
  /**
   * If the `valueFormat` is "fraction", this represents the valid tolerance of the measurement. Must be `1/(power of 2)`.
   *
   * @defaultValue 1/16
   */
  tolerance?: number;
};

type DecimalModeProps = {
  valueFormat: 'decimal';
  /**
   * If the `valueFormat` is "decimal" this represents the number of decimal places to allow.
   *
   * @required when `valueFormat` is "decimal".
   */
  tolerance: number;
};

export type InchesInputProps = BaseInchesInputProps & (FractionalModeProps | DecimalModeProps);

const measurementAsNumberRegex = (decimalPlaces?: number) =>
  new RegExp(`^-?(\\d*(\\.\\d{1,${decimalPlaces ?? ''}})?)$`);
const measurementAsFractionalRegex = /^-?(\d+ )?\d+\/\d+$/;

/**
 * Convert an imperial measurement to a number in inches, or null on failure.
 *
 * @example parseMeasurement("1-1/4", false, "fraction"); // returns 1.25
 *
 * @param value The string value to convert to a number
 * @param allowNegative If true, allows negative measurements
 * @param valueFormat Format to parse the measurement as
 * @param tolerance Allowable fractional tolerance (ie. 1/16), or number of decimal places (ie. 4) when `valueFormat` is "decimal"
 */
export const parseMeasurement = (
  value: string,
  allowNegative: boolean,
  valueFormat: 'fraction' | 'decimal',
  tolerance?: number,
): number | null => {
  const validDenominators: number[] = [];

  // If the value format is a fraction, build the list of valid denominators
  if (valueFormat === 'fraction' && isNotNil(tolerance)) {
    if (!hasValidTolerance(tolerance)) return null;
    for (let i = 1; i <= 1 / tolerance; i *= 2) {
      validDenominators.push(i);
    }
  }

  if (valueFormat === 'decimal' && isNotNil(tolerance)) {
    if (tolerance < 0 || tolerance > 100 || !Number.isInteger(tolerance)) return null;
  }

  // Remove excess "-" markers, spaces, and quotes
  const cleaned = value
    .replaceAll(/(?<!^)-/g, ' ')
    .replaceAll(/ +/g, ' ')
    .trim()
    .replace(/ *"$/, '');

  // Regardless of the final format type, the user can enter a fraction or a decimal.
  // This catches the fraction format.
  if (measurementAsFractionalRegex.test(cleaned)) {
    const [inches = '0', fractional = '0'] = (cleaned.indexOf(' ') < 0 ? `0 ${cleaned}` : cleaned).split(' ');
    const [num = 0, den = 1] = fractional.split('/').map(parseFloat);
    if (validDenominators.length === 0 || validDenominators.includes(den)) {
      const parsed = parseFloat(inches) + num / den;
      return Number.isNaN(parsed) || (!allowNegative && parsed < 0) ? null : parsed;
    }
  }

  // This handles the decimal format.
  if (measurementAsNumberRegex(valueFormat === 'fraction' ? undefined : tolerance).test(cleaned)) {
    const parsed = parseFloat(cleaned);
    if (!Number.isNaN(parsed) && (allowNegative || parsed >= 0)) {
      if (validDenominators.length === 0) return parsed;

      const validDecimals = validDenominators.reduce(
        (out, v) => {
          for (let i = 1; i < v; i++) out.add(i / v);
          return out;
        },
        new Set([0]),
      );

      if (validDecimals.has(Math.abs(parsed) % 1)) {
        return parsed;
      }
    }
  }

  return null;
};

/**
 * Used to allow a user to input inches using either decimals or fractional imperial units.
 * Only allows decimal and fractional for halves, quarters, eighths, sixteenths, and thirty-second inches.
 *
 * Accepts the following format examples:
 *
 * - 1.5
 * - .75
 * - 1 3/4
 * - 3-1/2"
 *
 * If an invalid input exists on blur, it will reset back to the previous valid input.
 */
export const InchesInput = ({
  value: parentValue,
  defaultValue = parentValue,
  onChange: parentOnChange,
  onValidChange,
  valueInFeet = false,
  valueFormat = 'fraction',
  allowNegative = false,
  allowBlank = false,
  blankValue,
  tolerance = 1 / 16,
  ...props
}: InchesInputProps) => {
  const multiplier = useMemo(() => (valueInFeet ? 12 : 1), [valueInFeet]);

  const convertToDisplayValue = useCallback(
    (val: number | null) =>
      val === null
        ? ''
        : valueFormat === 'fraction'
          ? convertInchesToImperialString(val, { tolerance })
          : `${Number(val.toFixed(tolerance))}"`,
    [valueFormat, tolerance],
  );

  const [value, setValue] = useState<string>(() =>
    isNotNil(defaultValue) ? convertToDisplayValue(defaultValue * multiplier) : '',
  );
  const [error, setError] = useState(false);

  useEffect(() => {
    if (isNotNil(parentValue)) {
      setValue((v) => {
        // Only update our local `value` if the actual numerical value of the passed in `parentValue`
        // is different from our own. This prevents rerendering while the user is still typing.
        const internalParsed = convertToDisplayValue(parseMeasurement(v, allowNegative, valueFormat, tolerance) ?? 0);
        const externalParsed = convertToDisplayValue(parentValue * multiplier);
        return internalParsed !== externalParsed ? externalParsed : v;
      });
    }
  }, [parentValue, multiplier, allowNegative, convertToDisplayValue, tolerance, valueFormat]);

  const [lastValidMeasurement, setLastValidMeasurement] = useState<number | null>(null);

  const onChange = (inches: number | null) => parentOnChange(inches === null ? null : inches / multiplier);

  return (
    <TextInput
      placeholder="Inches"
      {...props}
      error={props.error || error}
      onFocus={(...e) => {
        setLastValidMeasurement(parseMeasurement(value, allowNegative, valueFormat, tolerance));
        props.onFocus?.(...e);
      }}
      onBlur={(...e) => {
        if (isNotNil(blankValue) && value === '' && !allowBlank) {
          const newValue = blankValue * multiplier;
          setValue(convertToDisplayValue(newValue));
          onChange(newValue);
          setError(false);
          onValidChange?.(true);
        } else {
          const toUpdate = error
            ? lastValidMeasurement
            : parseMeasurement(value, allowNegative, valueFormat, tolerance);
          const isValid = isNotNil(toUpdate) || (allowBlank && value === '');
          if (isValid) {
            setValue(convertToDisplayValue(toUpdate));
            onChange(toUpdate);
            setError(false);
            onValidChange?.(true);
          }
        }
        props.onBlur?.(...e);
      }}
      value={value}
      onChange={({ currentTarget: { value: val } }) => {
        setValue(val);
        const measurement = parseMeasurement(val, allowNegative, valueFormat, tolerance);

        const isValid = isNotNil(measurement) || (allowBlank && val === '');
        setError(!isValid);
        onValidChange?.(isValid);

        if (isNotNil(measurement)) onChange(measurement);
      }}
    />
  );
};
